import { ThemeEvents } from '@theme/events';

class CartDeliveryProgress {
  /** @type {HTMLElement} */ el;
  /** @type {number} */ threshold;
  /** @type {number} */ deliveryThreshold;
  /** @type {boolean} */ giftEnabled;
  /** @type {HTMLElement | null} */ fill;
  /** @type {HTMLElement | null} */ message;
  /** @type {HTMLElement | null} */ labelCurrent;
  /** @type {HTMLElement | null} */ progressbar;

  /**
   * @param {HTMLElement} el - The root element of the progress bar component
   */
  constructor(el) {
    this.el = el;
    this.threshold = parseInt(el.dataset.threshold ?? '0', 10);
    this.deliveryThreshold = parseInt(el.dataset.deliveryThreshold ?? '0', 10);
    this.giftEnabled = el.dataset.giftEnabled === 'true';
    this.fill = el.querySelector('.js-delivery-progress-fill');
    this.message = el.querySelector('.js-delivery-progress-message');
    this.labelCurrent = el.querySelector('.js-delivery-progress-current');
    this.progressbar = el.querySelector('.js-delivery-progress-bar');

    const defaultTotal = parseInt(el.dataset.total ?? '0', 10);

    this.update(defaultTotal);
    this.events();
  }

  /**
   * Listens for cart updates and triggers a progress bar refresh
   */
  events() {
    const cartHandler = (/** @type {Event} */ e) => {
      const detail = /** @type {CustomEvent} */ (e).detail;
      const total = detail?.resource?.total_price;
      console.log('[progress-bar] event received:', e.type, '| total_price:', total);
      if (total !== undefined) this.update(total);
    };

    const discountHandler = (/** @type {Event} */ e) => {
      const total = /** @type {CustomEvent} */ (e).detail?.total_price;
      console.log('[progress-bar] delivery-progress:update | total_price:', total);
      if (total !== undefined) this.update(total);
    };

    document.addEventListener(ThemeEvents.cartUpdate, cartHandler);
    document.addEventListener('delivery-progress:update', discountHandler);
  }

  /**
   * Recalculates progress and updates all UI elements
   * @param {number} totalPence - Current cart total in pence
   */
  update(totalPence) {
    console.log('[progress-bar] update() called | totalPence:', totalPence, '| threshold:', this.threshold, '| fill el:', this.fill);
    if (!this.threshold) return;

    const remaining = Math.max(this.threshold - totalPence, 0);
    const percent = Math.min((totalPence / this.threshold) * 100, 100);

    console.log('[progress-bar] remaining:', remaining, '| percent:', percent);
    this.updateBar(percent);
    this.updateMessage(remaining, totalPence);
    this.updateCurrentLabel(totalPence, percent);

    this.el.dataset.total = String(totalPence);
  }

  /**
   * Updates the progress bar fill width and ARIA value
   * @param {number} percent - Progress percentage (0–100)
   */
  updateBar(percent) {
    if (this.fill) this.fill.style.width = `${percent}%`;
    if (this.progressbar) this.progressbar.setAttribute('aria-valuenow', String(Math.round(percent)));
  }

  /**
   * Updates the message text based on remaining amount and delivery/gift state
   * @param {number} remaining - Remaining amount in pence to reach the active threshold
   * @param {number} totalPence - Current cart total in pence
   */
  // updateMessage(remaining, totalPence) {
  //   if (!this.message) return;

  //   const deliveryUnlocked = this.giftEnabled && totalPence >= this.deliveryThreshold;

  //   if (remaining > 0) {
  //     this.message.innerHTML = deliveryUnlocked
  //       ? `<strong>${this.formatMoney(remaining)}</strong> away from free gift`
  //       : `<strong>${this.formatMoney(remaining)}</strong> away from free delivery`;
  //   } else if (deliveryUnlocked) {
  //     this.message.innerHTML = `You've unlocked <strong>free delivery</strong> and your <strong>free gift!</strong>`;
  //   } else {
  //     this.message.innerHTML = `You've unlocked <strong>free delivery!</strong>`;
  //   }
  // }

  updateMessage(remaining, totalPence) {
  if (!this.message) return;

  const giftThreshold = this.threshold; // main target = £100
  const hasFreeDelivery = totalPence >= this.deliveryThreshold;
  const hasFreeGift = this.giftEnabled && totalPence >= giftThreshold;

  if (!hasFreeDelivery) {
    const deliveryRemaining = Math.max(this.deliveryThreshold - totalPence, 0);

    this.message.innerHTML = `<strong>${this.formatMoney(deliveryRemaining)}</strong> away from free delivery`;
    return;
  }

  if (this.giftEnabled && !hasFreeGift) {
    const giftRemaining = Math.max(giftThreshold - totalPence, 0);

    this.message.innerHTML = `You've unlocked <strong>free delivery!</strong> Add <strong>${this.formatMoney(giftRemaining)}</strong> more for your free gift`;
    return;
  }

  if (hasFreeGift) {
    this.message.innerHTML = `You've unlocked <strong>free delivery</strong> and your <strong>free gift!</strong>`;
    return;
  }

  this.message.innerHTML = `You've unlocked <strong>free delivery!</strong>`;
}

  /**
   * Repositions and updates the floating current total label
   * @param {number} totalPence - Current cart total in pence
   * @param {number} percent - Progress percentage used to position the label
   */
  updateCurrentLabel(totalPence, percent) {
    if (!this.labelCurrent) return;
    this.labelCurrent.textContent = this.formatMoney(totalPence);
    this.labelCurrent.style.left = `${percent}%`;
    this.labelCurrent.style.display = percent >= 85 ? 'none' : '';
  }

  /**
   * Formats a pence value as a GBP string
   * @param {number} pence
   * @returns {string}
   */
  formatMoney(pence) {
    return '£' + (pence / 100).toFixed(2);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const el = document.querySelector('.js-delivery-progress');
  console.log('[progress-bar] DOMContentLoaded | el found:', el);
  if (el) new CartDeliveryProgress(/** @type {HTMLElement} */ (el));
});
